import Accordion from '@service-victoria/component-library/components/Accordion';
import { Button } from '@service-victoria/component-library/components/Button';
import Spinner from '@service-victoria/component-library/components/Icons/Spinner';
import List from '@service-victoria/component-library/components/List';
import Page from '@service-victoria/component-library/components/Page';
import Tabs, { TabItem } from '@service-victoria/component-library/components/Tabs';
import Text from '@service-victoria/component-library/components/Text';
import DJPRLogo from 'assets/djpr.jpg';
import bannerImg from 'assets/great-ocean-image.jpg';
import { app as appContext } from 'context';
import { useWindowSize } from 'hooks';
import useAvailable from 'hooks/useAvailable';
import { Availability } from 'interfaces/common';
import { AddAvailability } from 'interfaces/store';
import React from 'react';
import { useHistory } from 'react-router';
import { TextBodyWithInlineLink } from 'steps/styled';
import styled from 'styled-components';
import { constants, logger } from 'utils';
import FAQs from './FAQs';
import * as Styled from './styled';
import TooEarly from './TooEarly';
import Unavailable from './Unavailable';
import WhatYouNeed from './WhatYouNeed';

function homeContent(isAvailable: Availability | undefined, updateAvailability: AddAvailability) {
  const mainContent =
    isAvailable === 'TOO_EARLY' ? (
      <TooEarly updateAvailability={updateAvailability} />
    ) : (
      <React.Fragment>
        <Text.Body>
          The COVIDSafe Outdoor Activation Voucher Program gives eligible businesses a $2,000
          voucher for help pay to move existing activities outside.
        </Text.Body>
        <Text.Body>
          Applications open Wednesday 27 October 2021 at 11am. Vouchers will be allocated on a
          first-in basis to eligible businesses. Applications close when program funds are used
        </Text.Body>
        <Text.Body>Eligible businesses must:</Text.Body>
        <List
          type='disc'
          items={[
            'be a business, a charity/not-for-profit or a trader association',
            'operate in Victoria, except in the City of Melbourne',
            'have a plan to move their business activities outdoors',
            'will spend the money in less than 3 months from getting it',
            'have an active ABN and registered for GST',
            <Text.Link
              key='link'
              href='https://www.vic.gov.au/covidsafe-outdoor-activation-voucher-program'
              value='operate in an eligible industry'
            />,
            'have annual Victorian payroll under $3 million',
            'didn’t get support from the Outdoor Eating and Entertainment Package Program in 2020.'
          ]}
        />
        <Text.Body>
          <TextBodyWithInlineLink>
            <Text.Link
              href='https://www.vic.gov.au/covidsafe-outdoor-activation-voucher-program'
              value='vic.gov.au'
            />{' '}
            has a list of the full eligibility details.
          </TextBodyWithInlineLink>
        </Text.Body>
        <Text.Body>
          We need the most up to date, correct information for your business to check you’re
          eligible for a voucher. If your information isn’t correct, we can’t give you a voucher.
          We’ll use your ABN and information from the Australian Business Registry (ABR) to check if
          your business is eligible.
        </Text.Body>
        <Text.Body>
          If your application is successful, the Department of Jobs, Precincts and Regions (DJPR)
          will send you your COVIDSafe outdoor activation voucher. DJPR may verify the details
          you’ve supplied with other state and commonwealth agencies.
        </Text.Body>
        <Text.Body>
          <TextBodyWithInlineLink>
            We won’t use your information for any other purpose unless permitted by law. You can
            contact DJPR if you want to access your personal information.{' '}
            <Text.Link
              href='https://service.vic.gov.au/covid-19/outdoor-activation-voucher-collection-notice'
              value='Learn more.'
            />
          </TextBodyWithInlineLink>
        </Text.Body>
      </React.Fragment>
    );

  const items: TabItem[] = [
    {
      id: 'before-you-start',
      title: 'Before you start',
      component: mainContent
    },
    {
      id: 'what-you-need',
      title: "What you'll need",
      component: <WhatYouNeed />
    },
    {
      id: 'faq',
      title: 'FAQs',
      component: <FAQs />
    }
  ];

  return items;
}

const PageWrapper = styled(Page)``;

const Home: React.FC = () => {
  const history = useHistory();
  const windowSize = useWindowSize();
  const { currentState, actions } = appContext.useAppContext();
  const isAvailable = currentState.availability;
  const items = homeContent(isAvailable, actions.updateAvailability);
  // assume true?

  useAvailable();

  return (
    <PageWrapper
      title={constants.Page.title}
      showFooter={true}
      breadCrumbs={constants.breadCrumbs}
      imgUrl={bannerImg}
      loading={false}
      error={undefined}
      opacityBetweenLoading={0}
      headerProps={{
        fetchItemsFromAPI: true,
        showSideNav: true
      }}>
      {isAvailable === undefined && (
        <Styled.Loader>
          <Spinner />
        </Styled.Loader>
      )}
      {(isAvailable === 'SUCCESS' || isAvailable === 'TOO_EARLY') && (
        <Styled.Wrapper>
          <Styled.Header>
            <Styled.Description>
              Apply for a COVIDSafe Outdoor Activation Voucher
            </Styled.Description>
            <Styled.Line />
            <Styled.Timer>
              <Styled.Icon />
              <Styled.TextHead>
                This takes about
                <span>6 mins</span>
              </Styled.TextHead>
            </Styled.Timer>
          </Styled.Header>

          <Styled.DoubleLine />
          <Styled.Body>
            {windowSize === 'mobile' ? <Accordion items={items} /> : <Tabs items={items} />}
          </Styled.Body>

          {isAvailable === 'SUCCESS' && (
            <Styled.ButtonWrapper>
              <Button
                buttonType='primary'
                onClick={() => {
                  logger.info(`Start Transaction`, {
                    transactionID: currentState.transactionID
                  });
                  actions.startTransaction();
                  history.push(constants.Routes.Transaction);
                }}>
                Get Started
              </Button>
            </Styled.ButtonWrapper>
          )}

          <Styled.Logos>
            <Styled.VicLogo>
              A program of
              <img
                src={DJPRLogo}
                alt='Victoria State Government, in conjunction with the Department of Jobs, Precints and Regions'
              />
            </Styled.VicLogo>
          </Styled.Logos>
        </Styled.Wrapper>
      )}
      {isAvailable === 'TOO_LATE' && <Unavailable />}
    </PageWrapper>
  );
};

export default Home;
